import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../config/Firebase";

export function CreateAdmin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [branch, setBranch] = useState("");
  const [role, setRole] = useState("ADMIN");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const branchOptions = ["ChinnaSalem", "Thuraiyur", "Thammampatti"];
  const roleOptions = ["ADMIN", "USER"];

  // Animation variants (adapted from Middle theme)
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setMessage("Please fill all fields.");
      return;
    }

    // For ADMIN role, branch is forced to ADMIN. For USER role, branch must be selected.
    const branchToSave = role === "ADMIN" ? "ADMIN" : branch;
    if (role === "USER" && !branchToSave) {
      setMessage("Please select a branch for USER role.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      await setDoc(doc(db, "LDB", username), {
        username,
        password,
        branch: branchToSave,
        role,
      });
      setMessage("Admin created successfully!");
      setTimeout(() => navigate("/Middle"), 2000); // Redirect after success
    } catch (error) {
      setMessage("Error creating admin: " + error.message);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 sm:p-8">
      {/* Header (same theme as Middle) */}
      <motion.h1
        variants={headerVariants}
        initial="hidden"
        animate="visible"
        className="text-4xl sm:text-5xl font-extrabold text-black mb-10 relative"
      >
        Create New Admin
        <span className="absolute bottom-0 left-0 w-1/3 h-1 bg-black rounded-full" />
      </motion.h1>

      {/* Form Container (themed like dashboard cards) */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-md w-full relative group p-6 rounded-xl bg-black text-white shadow-md overflow-hidden"
      >
        {/* Hover Overlay Effect (adapted for form) */}
        <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl pointer-events-none" />

        {/* Subtle Border Animation */}
        <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/30 rounded-xl transition-all duration-300 pointer-events-none" />

        {message && (
          <div className={`p-3 rounded-md mb-4 ${
            message.includes("successfully") 
              ? "bg-green-900/80 text-green-100 border border-green-500/30" 
              : "bg-red-900/80 text-red-100 border border-red-500/30"
          }`}>
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 relative z-10">
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-white/80 mb-1">
              Role
            </label>
            <select
              id="role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-white/50 text-white"
              required
            >
              {roleOptions.map((option) => (
                <option key={option} value={option} className="text-black">
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="username" className="block text-sm font-medium text-white/80 mb-1">
              Username
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-white/50 text-white placeholder-white/50"
              placeholder="Enter username"
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-white/80 mb-1">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-white/50 text-white placeholder-white/50"
              placeholder="Enter password"
              required
            />
          </div>

          {role === "USER" && (
            <div>
              <label htmlFor="branch" className="block text-sm font-medium text-white/80 mb-1">
                Branch
              </label>
              <select
                id="branch"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-white/50 text-white"
                required
              >
                <option value="" className="text-black">Select a branch</option>
                {branchOptions.map((option) => (
                  <option key={option} value={option} className="text-black">
                    {option}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-white text-black py-2 px-4 rounded-md hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-white/50 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Creating..." : "Create Admin"}
          </button>
        </form>

        <button
          onClick={() => navigate("/Middle")}
          className="w-full mt-4 text-white/80 hover:text-white transition-colors duration-300 text-sm font-medium"
        >
          Back to Dashboard
        </button>
      </motion.div>
    </div>
  );
}